import { Badge } from "@/components/ui/badge";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { Suggestion } from "@/types/document";
import { classifications, ClassificationType } from "@/types/config";
import { blockLabel } from "./blockUtils";
import { cn } from "@/lib/utils";

interface BlockReferencesProps {
  blockId: string,
  suggestion: Suggestion;
  focusMode?: boolean;
  isSelected?: boolean;
}

export const BlockReferences = ({ suggestion, blockId, focusMode = false, isSelected = true }: BlockReferencesProps) => {
  const group = useAppState((state) => state.selectedGroup)!;
  const document = useAppState((state) => state.currentDocument)!;
  const currentRubric = useAppState((state) => state.currentRubric);
  const selectedBlockIndex = useAppState((state) => state.selectedBlockIndex);
  const dispatch = useAppDispatch();

  const handleClick = () => {
    dispatch({
      type: "SHOW_REFERENCE_PANEL",
      payload: { visible: true, view: "references" }
    });
    
    const newBlockIndex = group.blockIds.indexOf(blockId);
    if (newBlockIndex !== selectedBlockIndex) {
      dispatch({
        type: "SET_SELECTED_BLOCK_INDEX",
        payload: newBlockIndex,
      });
    }
  };

  const isDimmed = focusMode && !isSelected;

  const badge =
    suggestion.blocks.length === 1 ? (
      <Badge className={cn("font-semibold bg-zinc-50 text-zinc-500", isDimmed && "opacity-30")}>
        {blockLabel(document, blockId)}
      </Badge>
    ) : (
      <Badge
        className={cn("cursor-pointer bg-blue-50 text-blue-600", isDimmed && "opacity-30")}
        onClick={handleClick}
      >
        Multiple
      </Badge>
    );

  const rubricCriteria = currentRubric?.criteria.find(
    (criteria) => criteria.id === suggestion.rubricCriteriaId
  );

  return (
    <div className="px-2 -mt-1 pb-3 flex items-center">
      <span className={cn("text-xs text-muted-foreground mr-2", isDimmed && "text-gray-300")}>Refers to</span>
      {badge}
      {rubricCriteria && (
        <Badge variant="secondary" className={cn("border ml-auto bg-zinc-50 text-zinc-500 font-semibold", isDimmed && "opacity-30")}>
          <span className="max-w-[400px] truncate">
          {rubricCriteria.criteria}</span>
        </Badge>
      )}
      {suggestion.classification && (
        <Badge variant="secondary" className={cn("border ml-auto bg-zinc-50 text-zinc-500 font-semibold", isDimmed && "opacity-30")}>
          {classifications[suggestion.classification as ClassificationType].label}
        </Badge>
      )}
    </div>
  );
};
